import confetti from 'canvas-confetti';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef } from 'react';

import type { ExpiryEvent } from '@services/accountService';
import { brandColors } from '@utils/color-utils';

interface OptionExpiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  expiryEvent: ExpiryEvent;
}

export function OptionExpiryModal({
  isOpen,
  onClose,
  expiryEvent,
}: OptionExpiryModalProps) {
  const isProfit = expiryEvent.totalPnL > 0;
  const pepeImage = isProfit ? '/pepe-happy.webp' : '/peeposadgecry.webp';
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const formatPnL = (pnl: number) => {
    const absValue = Math.abs(pnl);
    if (absValue >= 1000) {
      return `${pnl >= 0 ? '+' : ''}$${(pnl / 1000).toFixed(1)}k`;
    }
    return `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}`;
  };

  const formatPrice = (price: number) => {
    if (price >= 1000) {
      return `$${(price / 1000).toFixed(1)}k`;
    }
    return `$${price.toFixed(2)}`;
  };

  const totalPositions = expiryEvent.groups.reduce(
    (sum, g) => sum + g.positionCount,
    0,
  );

  useEffect(() => {
    if (!isOpen) {
      confetti.reset();
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      return;
    }

    if (isProfit) {
      const colors = [brandColors.green];
      const createRisingConfetti = (delay: number) => {
        const timeout = setTimeout(() => {
          confetti({
            particleCount: 1,
            angle: 90,
            spread: 15,
            origin: { x: Math.random(), y: Math.random() },
            colors,
            gravity: -1,
            scalar: 0.5,
            drift: 0,
            startVelocity: 0,
            ticks: 50,
          });
        }, delay);
        timeoutsRef.current.push(timeout);
      };

      for (let i = 0; i < 12; i++) {
        createRisingConfetti(i * 200);
      }
    } else {
      const colors = [brandColors.red];
      const createSnowfall = (delay: number) => {
        const timeout = setTimeout(() => {
          confetti({
            particleCount: 1,
            angle: 90,
            spread: 15,
            origin: { x: Math.random(), y: Math.random() },
            colors,
            gravity: 1,
            scalar: 0.35,
            drift: 0,
            startVelocity: 0,
            ticks: 50,
          });
        }, delay);
        timeoutsRef.current.push(timeout);
      };

      for (let i = 0; i < 12; i++) {
        createSnowfall(i * 200);
      }
    }

    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [isOpen, isProfit, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-dark/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-brand-dark/90 border border-white/20 rounded-2xl p-6 max-w-sm w-full mx-4 text-center max-h-[80vh] overflow-y-auto"
            onClick={onClose}
          >
            {/* Pepe Image */}
            <div className="flex justify-center mb-4">
              <img
                src={pepeImage}
                alt={isProfit ? 'Happy Pepe' : 'Sad Pepe'}
                className="w-20 h-20 rounded-full"
              />
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-white mb-2">
              {isProfit
                ? 'Options Expired In-The-Money!'
                : 'Options Expired Out-Of-The-Money'}
            </h2>

            {/* Total PnL */}
            <div className="mb-4">
              <div className="text-white/60 text-sm mb-1">
                Total Realized PnL
              </div>
              <div
                className={`text-2xl font-bold ${isProfit ? 'text-green-400' : 'text-red-400'}`}
              >
                {formatPnL(expiryEvent.totalPnL)}
              </div>
            </div>

            {/* Position Groups */}
            <div className="space-y-3 mb-4">
              {expiryEvent.groups.map((group, index) => (
                <div
                  key={index}
                  className="bg-white/5 rounded-lg p-3 text-left"
                >
                  <div className="flex justify-between items-center mb-1">
                    <div className="text-white font-medium">
                      {group.positionCount} {group.asset} {group.positionType}
                    </div>
                    <div
                      className={`text-sm font-bold ${
                        group.realizedPnL >= 0
                          ? 'text-green-400'
                          : 'text-red-400'
                      }`}
                    >
                      {formatPnL(group.realizedPnL)}
                    </div>
                  </div>
                  <div className="text-white/60 text-xs">
                    Strike: {formatPrice(group.averageStrike)} • Final:{' '}
                    {formatPrice(group.expiryPrice)}
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="text-white/60 text-sm mb-4">
              Settled {totalPositions} position{totalPositions > 1 ? 's' : ''}{' '}
              across {expiryEvent.groups.length} group
              {expiryEvent.groups.length > 1 ? 's' : ''}
            </div>

            {/* Subtitle */}
            <div
              className={`text-sm ${isProfit ? 'text-green-400' : 'text-red-400'}`}
            >
              {isProfit ? 'Perfect timing! 🎉' : 'Better luck next round 💔'}
            </div>

            {/* Tap to dismiss hint */}
            <div className="text-white/40 text-xs mt-4">
              Tap anywhere to dismiss
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
